import {Component, computed, EventEmitter, inject, Input, input, OnDestroy, OnInit, Output, signal} from '@angular/core';
import {TranslationService} from "../../../../core/services/translation.service";
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators} from "@angular/forms";
import {CustomValidators} from "../../../../core/validators";
import {LoginService} from "../../../../core/components/login/login.service";
import {ConfigService} from "../../../../core/services/config.service";
import {MatStepper} from "@angular/material/stepper";
import {HttpClient} from "@angular/common/http";
import {ValidationPattern} from "../../../../core/models/ValidationPattern";
import {Router} from "@angular/router";
import {OrderProcessingService} from "../../../../data/services/order-processing.service";
import {MatError, MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatCard, MatCardContent} from "@angular/material/card";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {RegistrationValue} from "../../../../data/models/subscription-order.model";
import {distinctUntilChanged, Subscription} from "rxjs";

@Component({
  selector: 'app-registration-step',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatCardContent,
    MatFormField,
    MatProgressSpinner,
    MatLabel,
    MatIcon,
    MatError,
    MatCard,
    MatButton
  ],
  templateUrl: './registration-step.component.html',
  styleUrl: './registration-step.component.css'
})
export class RegistrationStepComponent implements OnInit, OnDestroy {
  protected translationService: TranslationService = inject(TranslationService);
  private orderProcessService: OrderProcessingService = inject(OrderProcessingService);
  private loginService: LoginService = inject(LoginService);
  private configService: ConfigService = inject(ConfigService);
  private httpClient = inject(HttpClient);
  private router = inject(Router);

  private passwordInputValidators: ValidatorFn[] = [Validators.required];

  private formBuilder = new FormBuilder();
  protected loginFormGroup: FormGroup;

  private lastEmailInput = '';

  public isEditable = input<boolean>();
  public isAuthenticated = input<boolean>();
  public hideLoginBox = input<boolean>();

  @Input() parentStepper!: MatStepper;
  @Output() stepReady = new EventEmitter<boolean>();

  private registerPasswordVisibleConf = signal(false);
  public registerPasswordVisible = signal(false);
  public showPasswordInput = computed(() => this.registerPasswordVisibleConf() && this.registerPasswordVisible());
  private passwordComplexity = signal(null);
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.loadConfigs();
    this.buildRegisterForm();
    this.setPasswordValidators();
    this.observeChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadConfigs() {
    //Muss das Password abgefragt werden?
    this.subscriptions.add(this.configService.loadConfig('offer.register.showPassword', true).subscribe(config => {
      const raw = String(config?.value ?? '').trim().toLowerCase();
      const enabled = raw === '1' || raw === 'true';
      this.registerPasswordVisibleConf.set(enabled);
      if (!enabled) {
        this.resetPasswordInputState();
      }
    }));
  }

  private observeChanges() {
    //Daten aus der Form direkt an den OrderProcessService schicken
    this.subscriptions.add(this.orderProcessService.connectForm<RegistrationValue>(
      this.loginFormGroup,
      state => state,
      (state, value) => ({
        ...state,
        registration: value
      })));

    this.subscriptions.add(this.loginFormGroup.statusChanges.subscribe(status => {
      //Somit markiert die Parent-Komponente den Step als completed und der Nutzer kann weiter steppen
      this.stepReady.emit(status === 'VALID');
    }));

    this.subscriptions.add(this.loginFormGroup.get('email').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      // Email change should reset optional password flow and avoid stale password state on back navigation.
      this.resetPasswordInputState();
    }));
  }

  protected getPasswordComplexityHint(): string {
    let firstPart = '', middlePart = [], lastPart = '';
    const pwCpx = this.passwordComplexity();

    // ToDo: translations
    if (pwCpx) {
      if (parseInt(pwCpx.minLength)) {
        firstPart = 'mindestens ' + pwCpx.minLength + ' Zeichen';
      }
      if (parseInt(pwCpx.lowerChars)) {
        middlePart.push(pwCpx.lowerChars + ' Kleinbuchstaben');
      }
      if (parseInt(pwCpx.upperChars)) {
        middlePart.push(pwCpx.upperChars + ' Großbuchstaben');
      }
      if (parseInt(pwCpx.digits)) {
        middlePart.push(pwCpx.digits + ' Ziffern');
      }
      if (parseInt(pwCpx.specialChars)) {
        middlePart.push(pwCpx.specialChars + ' Sonderzeichen');
      }
      lastPart = middlePart.length > 1 ? middlePart.pop() : lastPart;
    }

    return firstPart + (firstPart.length && middlePart.length ? ', bestehend aus ' : '') + middlePart.join(', ') + (lastPart.length ? ' und ' : '') + lastPart;
  }

  protected getPasswordErrorMessage(): string {
    let msg = [];
    const pw = this.loginFormGroup.get('password');
    ['minLength', 'lowerChars', 'upperChars', 'digits', 'specialChars'].forEach(k => {
      pw.hasError(k) ? msg.push(pw.getError(k)) : null;
    });

    return (msg.length ? 'noch ' : '') + msg.join(', ');
  }

  private setPasswordValidators(): void {
    // ToDo: translations
    this.loginService.getPasswordComplexityInfo().subscribe(data => {
      if (data) {
        this.passwordComplexity.set(data);
        if (parseInt(data.minLength)) {
          this.passwordInputValidators.push((c: AbstractControl) => {
            const missing = Math.max(0, parseInt(data.minLength) - c.value.length);
            return missing ? {minLength: missing + ' Zeichen'} : null;
          });
        }
        if (parseInt(data.lowerChars)) {
          this.passwordInputValidators.push((c: AbstractControl) => {
            const missing = Math.max(0, parseInt(data.lowerChars) - c.value.replace(/[^a-zäöüáéíóúýàèìòùâêîôûßčěňřšůž]/g, '').length);
            return missing ? {lowerChars: missing + ' Kleinbuchstabe' + (missing == 1 ? '' : 'n')} : null;
          });
        }
        if (parseInt(data.upperChars)) {
          this.passwordInputValidators.push((c: AbstractControl) => {
            const missing = Math.max(0, parseInt(data.upperChars) - c.value.replace(/[^A-ZÄÖÜÁÉÍÓÚÝÀÈÌÒÙÂÊÎÔÛĎČĚŇŘŠŤŮŽ]/g, '').length);
            return missing ? {upperChars: missing + ' Großbuchstabe' + (missing == 1 ? '' : 'n')} : null;
          });
        }
        if (parseInt(data.digits)) {
          this.passwordInputValidators.push((c: AbstractControl) => {
            const missing = Math.max(0, parseInt(data.digits) - c.value.replace(/[^0-9]/g, '').length);
            return missing ? {digits: missing + ' Ziffer' + (missing == 1 ? '' : 'n')} : null;
          });
        }
        if (parseInt(data.specialChars)) {
          this.passwordInputValidators.push((c: AbstractControl) => {
            const missing = Math.max(0, parseInt(data.specialChars) - c.value.replace(/[0-9a-zäöüáéíóúýàèìòùâêîôûßčěňřšůžA-ZÄÖÜÁÉÍÓÚÝÀÈÌÒÙÂÊÎÔÛĎČĚŇŘŠŤŮŽ]/g, '').length);
            return missing ? {specialChars: missing + ' Sonderzeichen'} : null;
          });
        }
      }
    });
  }

  private buildRegisterForm(): void {
    this.loginFormGroup = this.formBuilder.group(
      {
        email: [
          this.lastEmailInput.length ? this.lastEmailInput : '', [
            Validators.required,
            Validators.pattern(ValidationPattern.EMAIL)
          ]],
        password: ['', []]
      },
      {
        validators: [CustomValidators.checkRegisterForm(this.registerPasswordVisibleConf())]
      }
    );
    this.loginFormGroup.get('password').disable();
  }

  protected onRegisterNext(stepper: MatStepper): void {
    const passwordControl = this.loginFormGroup.get('password');
    const emailControl = this.loginFormGroup.get('email');
    if (!this.registerPasswordVisible()) {
      emailControl.markAsPending();
    }
    const emailToCheck = emailControl.value;
    this.lastEmailInput = emailToCheck;

    CustomValidators.checkIfEmailExists(emailToCheck, this.httpClient, this.translationService)
      .subscribe(data => {
        emailControl.updateValueAndValidity(); // hide input-spinner
        if (data !== null) {
          this.router.navigate(['/login'], {queryParams: {returnUrl: this.router.url, prefillUsername: emailToCheck}})
            .then(() => null);
        } else {
          if (!this.registerPasswordVisibleConf()) {
            this.resetPasswordInputState();
            this.goToNextStep(stepper);
            return;
          }

          this.registerPasswordVisible.set(true);
          if (this.showPasswordInput()) {
            let firstTry = !passwordControl.hasValidator(Validators.required);
            if (!firstTry) {
              passwordControl.updateValueAndValidity({onlySelf: true});
            }
            if (passwordControl.valid) {
              this.goToNextStep(stepper);
            } else if (firstTry) {
              setTimeout(() => {
                passwordControl.enable();
                passwordControl.setValidators(this.passwordInputValidators);
                passwordControl.setErrors(null);
              }, 0);
            }
          }
        }
      });
  }

  private goToNextStep(stepper: MatStepper): void {
    // Wait one tick so form status can settle from PENDING to VALID before linear stepper navigation.
    setTimeout(() => stepper.next(), 0);
  }

  private resetPasswordInputState(): void {
    const passwordControl = this.loginFormGroup.get('password');
    this.registerPasswordVisible.set(false);
    passwordControl.clearValidators();
    passwordControl.clearAsyncValidators();
    passwordControl.setValue('', {emitEvent: false});
    passwordControl.disable({emitEvent: false});
    passwordControl.updateValueAndValidity({emitEvent: false, onlySelf: true});
  }
}
